import React from 'react';
import { Stack } from 'react-bootstrap';
import { Cell } from 'recharts';
import { BLUE_COLORS, GREEN_YELLOW_RED_COLORS } from '../../client-dashboard/candidate-scoring-util';
import OpenEndedComments from '../leader/open-ended-comments';
import { REPORT_TYPE } from '../../../utils/constants';

export const MAX_VALUES = 18;

export const valuesRatingBreakpoints = {
  breaks: [3, 2],
  scale: 3,
};

const ORDINAL_RULES = new Intl.PluralRules('en', { type: 'ordinal' });
const SUFFIXES = {
  one: 'st',
  two: 'nd',
  few: 'rd',
  other: 'th',
};

const getPercentileSuffix = (value) => {
  const category = ORDINAL_RULES.select(value);
  return SUFFIXES[category];
};

export const getLeftBarColorClassPercentile = (value, reportType = REPORT_TYPE.coaching) => {
  const classPrefix = reportType !== REPORT_TYPE.coaching ? 'develop-' : 'select-';
  if (value >= 66) {
    return `${classPrefix}chart-bar-left-cutoff-high`;
  }
  if (value >= 33) {
    return `${classPrefix}chart-bar-left-cutoff-mid`;
  }
  return `${classPrefix}chart-bar-left-cutoff-low`;
};

export const getRightBarColorClassPercentile = (value, reportType = REPORT_TYPE.coaching) => {
  const classPrefix = reportType !== REPORT_TYPE.coaching ? 'develop-' : 'select-';
  if (value >= 66) {
    return `${classPrefix}chart-bar-right-cutoff-high`;
  }
  if (value >= 33) {
    return `${classPrefix}chart-bar-right-cutoff-mid`;
  }
  return `${classPrefix}chart-bar-right-cutoff-low`;
};

export const getLeftBarColorClassCutoffs = (value, breaks, reportType = REPORT_TYPE.coaching) => {
  const classPrefix = reportType !== REPORT_TYPE.coaching ? 'develop-' : 'select-';
  if (value >= breaks[0]) {
    return `${classPrefix}chart-bar-left-cutoff-high`;
  }
  if (value >= breaks[1]) {
    return `${classPrefix}chart-bar-left-cutoff-mid`;
  }
  return `${classPrefix}chart-bar-left-cutoff-low`;
};

export const getRightBarColorClassCutoffs = (value, breaks, reportType = REPORT_TYPE.coaching) => {
  const classPrefix = reportType !== REPORT_TYPE.coaching ? 'develop-' : 'select-';
  if (value >= breaks[0]) {
    return `${classPrefix}chart-bar-right-cutoff-high`;
  }
  if (value >= breaks[1]) {
    return `${classPrefix}chart-bar-right-cutoff-mid`;
  }
  return `${classPrefix}chart-bar-right-cutoff-low`;
};

export const getAssessmentContentPercentile = (
  title,
  value,
  reportType = REPORT_TYPE.coaching,
  displayPercentile = false
) => {
  return (
    <Stack>
      <div className="text-bold">{title}</div>
      <Stack className="chart-data" direction="horizontal">
        <div style={{ width: displayPercentile ? '50%' : '75%' }}>
          <div className="progress-chart">
            <div
              className={`chart-bar chart-bar-left ${getLeftBarColorClassPercentile(value, reportType)}`}
              style={{ width: `${value}%` }}
            />
            <div
              className={`chart-bar chart-bar-right ${getRightBarColorClassPercentile(value, reportType)}`}
              style={{ width: `${100 - value}%` }}
            />
          </div>
        </div>
        {displayPercentile && (
          <div className="d-flex h-100 ps-2 align-items-center">
            <div className="text-bolder text-end">
              {value}
              <sup>{getPercentileSuffix(value)}</sup>&nbsp;&nbsp;percentile
            </div>
          </div>
        )}
      </Stack>
    </Stack>
  );
};

const getChartBarLeftClassname = (value, divisor) => {
  const widthValue = value * divisor;
  let leftClassName = 'chart-bar-left';
  if (widthValue === 100) {
    leftClassName = 'chart-bar-full';
  }

  return leftClassName;
};

export const getAssessmentContentBlank = (title, value, breakpoints, reportType = REPORT_TYPE.coaching) => {
  let breaks = [4.25, 3.0];
  let scale = 5;
  if (breakpoints) {
    breaks = breakpoints.breaks;
    scale = breakpoints.scale;
  }

  const divisor = 100 / scale;

  return (
    <Stack>
      {title?.length > 0 ? <div className="text-bolder w-75">{title}</div> : <div style={{ height: '1em' }} />}
      <Stack className="chart-data" direction="horizontal">
        <div style={{ width: '80%' }}>
          <div className="progress-chart">
            <div
              className={`subscore-chart-bar ${getChartBarLeftClassname(value, divisor)} ${getLeftBarColorClassCutoffs(
                value,
                breaks,
                reportType
              )}`}
              style={{
                width: `${value * divisor}%`,
              }}
            />
            <div
              className={`subscore-chart-bar chart-bar-right ${getRightBarColorClassCutoffs(
                value,
                breaks,
                reportType
              )}`}
              style={{
                width: `${100 - value * divisor}%`,
              }}
            />
          </div>
        </div>
      </Stack>
    </Stack>
  );
};
export const getAssessmentContentCutoffs = (title, value, breakpoints, reportType = REPORT_TYPE.coaching) => {
  let breaks = [4.25, 3.0];
  let scale = 5;
  if (breakpoints) {
    breaks = breakpoints.breaks;
    scale = breakpoints.scale;
  }

  const divisor = 100 / scale;

  return (
    <Stack>
      <div className="text-bolder w-75">{title}</div>
      <Stack className="chart-data" direction="horizontal">
        <div style={{ width: '80%' }}>
          <div className="progress-chart">
            <div
              className={`subscore-chart-bar ${getChartBarLeftClassname(value, divisor)} ${getLeftBarColorClassCutoffs(
                value,
                breaks,
                reportType
              )}`}
              style={{
                width: `${value * divisor}%`,
              }}
            />
            <div
              className={`subscore-chart-bar chart-bar-right ${getRightBarColorClassCutoffs(
                value,
                breaks,
                reportType
              )}`}
              style={{
                width: `${100 - value * divisor}%`,
              }}
            />
          </div>
        </div>
        <div className="d-flex h-100 align-items-center ps-2">
          <div className="text-bolder text-end">{value ? `${value.toFixed(1)}` : ''}</div>
        </div>
      </Stack>
    </Stack>
  );
};

export const getAssessmentText = (title, value) => (
  <div>
    {!title || !Array.isArray(title) ? (
      <div>{title}</div>
    ) : (
      title.map((t) => {
        return (
          <div className="pb-2" key={Math.random()}>
            {t}
          </div>
        );
      })
    )}
    {!value || !Array.isArray(value) ? (
      <div style={{ fontSize: '.85em' }}>{value}</div>
    ) : (
      value.map((v) => {
        return (
          <div className="pb-2" style={{ fontSize: '.85em' }} key={Math.random()}>
            {v}
          </div>
        );
      })
    )}
  </div>
);

export const getCandidateLeaderText = (isCandidate, capitalize, plural) => {
  let value;
  if (isCandidate) {
    value = 'candidate';
  } else {
    value = 'individual';
  }

  if (capitalize) {
    value = value.charAt(0).toUpperCase() + value.slice(1);
  }
  if (plural) {
    value += 's';
  }
  return value;
};

export const getCandidateHonestyHumilityText = (isCandidate) => {
  if (isCandidate) {
    return 'Honesty-Humility';
  } else {
    return 'Humility';
  }
};
export const getCandidateExtraversionText = (isCandidate, capitalize) => {
  let value;
  if (isCandidate) {
    value = 'extraversion';
  } else {
    value = 'sociability';
  }

  if (capitalize) {
    value = value.charAt(0).toUpperCase() + value.slice(1);
  }

  return value;
};
export const updateValuesOverallText = (isCandidate, text) => {
  if (text && isCandidate) {
    return text.replace('work values', 'values');
  }

  return text;
};

export const getCandidateValuesText = (isCandidate, capitalize) => {
  let value;
  if (isCandidate) {
    value = 'Values';
  } else {
    value = 'Work Values';
  }

  if (!capitalize) {
    value = value.toLowerCase();
  }

  return value;
};
export const getCandidatePerformanceText = (isCandidate, withSuffix = false) => {
  let value;
  if (isCandidate) {
    value = 'Performance';
  } else {
    value = 'Leader Performance Potential';
  }

  if (withSuffix) {
    value = value + ' (LPP)';
  }

  return value;
};
export const getCandidateLoyaltyText = (isCandidate, withSuffix = false) => {
  let value;
  if (isCandidate) {
    value = 'Loyalty';
  } else {
    value = 'Team Retention Potential';
  }

  if (withSuffix) {
    value = value + ' (TRP)';
  }

  return value;
};
export const getTipContent = (title, value, description, breakpoints, reportType = REPORT_TYPE.coaching) => {
  let breaks = [4.25, 3.0];
  if (breakpoints) {
    breaks = breakpoints.breaks;
  }

  return (
    <Stack direction="horizontal" className="align-items-start">
      <Stack className="chart-data tips-bullet">
        <div className={getTipDotStyle(value, breaks, reportType)} />
      </Stack>
      <div className="pt-0" style={{ alignItems: 'start' }}>
        <span style={{ fontWeight: 'bold' }}>{title}: </span>
        {description}
      </div>
    </Stack>
  );
};

export const getTipDotStyle = (value, breaks, reportType = REPORT_TYPE.coaching) => {
  const classPrefix = reportType !== REPORT_TYPE.coaching ? 'develop-' : 'select-';

  let dotStyle = 'tips-dot';

  if (breaks.length === 0) {
    dotStyle += ' tips-dot-not-rated';
  } else {
    if (value >= breaks[0]) {
      dotStyle += ` ${classPrefix}tips-dot-r`;
    } else if (value >= breaks[1]) {
      dotStyle += ` ${classPrefix}tips-dot-rwc`;
    } else {
      dotStyle += ` ${classPrefix}tips-dot-dnr`;
    }
  }

  return dotStyle;
};

export const getRandomTip = (arr) => {
  return arr.splice((Math.random() * arr.length) | 0, 1);
};

export const shuffleArray = (candidate, unshuffled) => {
  const factor = candidate.name.length;
  const remainder = factor % unshuffled.length;
  return unshuffled
    .map((value, index) => {
      const sort = index >= remainder ? index - remainder : index + unshuffled.length;
      return {
        value,
        sort,
      };
    })
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);
};

export const getTip = (score, tip, breakpoints) => (
  <>
    <Stack direction="horizontal" className="pb-2">
      <div>{getTipContent(tip?.title, score, tip?.text, breakpoints)}</div>
    </Stack>
  </>
);

export const getConditionalExperienceTip = (score, title, text, reportType = REPORT_TYPE.coaching) => (
  <>
    {score < 4 ? (
      <>
        <Stack direction="horizontal" className="pb-2" key={Math.random()}>
          <div>{getTipContent(title, score, text, undefined, reportType)}</div>
        </Stack>
      </>
    ) : (
      ''
    )}
  </>
);

export const getScoreColors = (score, reportType) => {
  const colorPalette = reportType === REPORT_TYPE.development ? BLUE_COLORS : GREEN_YELLOW_RED_COLORS;
  if (score >= 66) {
    return (
      <>
        <Cell fill={colorPalette.highlyRecommend.primary} />
        <Cell fill={colorPalette.highlyRecommend.secondary} />
      </>
    );
  }
  if (score >= 33) {
    return (
      <>
        <Cell fill={colorPalette.recommendWithCaution.primary} />
        <Cell fill={colorPalette.recommendWithCaution.secondary} />
      </>
    );
  }
  return (
    <>
      <Cell fill={colorPalette.doNotRecommend.primary} />
      <Cell fill={colorPalette.doNotRecommend.secondary} />
    </>
  );
};

export const reportCompetencies = (candidate) => {
  if (candidate) {
    if (candidate.selfCompetencies?.scores && candidate.raters?.submitted >= 3) {
      return true;
    }
  }

  return false;
};

export const generateCompetenciesCommentsPdfPages = (creatingPdf, candidate) => {
  const strengthsLength = candidate.selfCompetencies?.scores.greatestStrengthCollection?.length;
  const improvementLength = candidate.selfCompetencies?.scores.opportunityForImprovementCollection?.length;

  if (!creatingPdf || !strengthsLength || !improvementLength) {
    return <></>;
  }

  const COMMENTS_PER_PAGE = 22;

  const components = [];
  if (strengthsLength + improvementLength < COMMENTS_PER_PAGE) {
    components.push(
      <div className="print-page">
        <OpenEndedComments candidate={candidate} asPage={true} allComments={true} />
      </div>
    );
  } else {
    let nPages = Math.ceil(strengthsLength / COMMENTS_PER_PAGE);
    for (let p = 0; p < nPages; p++) {
      const commentsStart = p * COMMENTS_PER_PAGE;
      const commentsEnd = commentsStart + COMMENTS_PER_PAGE;
      components.push(
        <div className="print-page">
          <OpenEndedComments
            candidate={candidate}
            asPage={true}
            strengths={true}
            start={commentsStart}
            end={commentsEnd}
          />
        </div>
      );
    }
    nPages = Math.ceil(improvementLength / COMMENTS_PER_PAGE);
    for (let p = 0; p < nPages; p++) {
      const commentsStart = p * COMMENTS_PER_PAGE;
      const commentsEnd = commentsStart + COMMENTS_PER_PAGE;
      components.push(
        <div className="print-page">
          <OpenEndedComments
            candidate={candidate}
            asPage={true}
            strengths={false}
            start={commentsStart}
            end={commentsEnd}
          />
        </div>
      );
    }
  }

  return components;
};
